import { Stack, Tooltip } from "@mui/material";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { useOmnisearch } from "@src/appV2/Omnisearch";
import { useNavHeader } from "../../Shared/Header/useNavHeader";
import { Button, type ButtonVariant } from "@src/appV2/redesign/components/Button";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { NotificationsCenterDrawer } from "@src/appV2/redesign/AppLayout/Shared/NotificationsCenterDrawer";
import { Badge } from "@src/appV2/redesign/components/Badge";
import { useIsRebrand } from "@src/appV2/redesign/theme/RebrandProvider";
import { ThemeModeToggle } from "@src/appV2/UserPreferences/components/ThemeModeToggle";
import { useThemeMode } from "@src/appV2/UserPreferences/UserPreferencesProvider";
import { useSwitchToAdmin } from "../../Shared/AccountSwitcher/useSwitchToAdmin";

interface NavigationMenuFooterMobileProps {
  closeSidenav: () => void;
}

interface AdminActionsProps {
  closeSidenav: () => void;
  navLinkVariant: ButtonVariant;
  displayNotificationsButton: boolean;
  iconTypeNotificationsButton: ReturnType<typeof useNavHeader>["iconTypeNotificationsButton"];
  unreadNotificationCount: number;
  onClickNotificationsButton: () => void;
}

function AdminActions(props: AdminActionsProps) {
  const {
    closeSidenav,
    navLinkVariant,
    displayNotificationsButton,
    iconTypeNotificationsButton,
    unreadNotificationCount,
    onClickNotificationsButton,
  } = props;
  const { isImpersonating, switchToAdmin } = useSwitchToAdmin();
  const { open: openOmnisearch } = useOmnisearch();

  return (
    <>
      {isImpersonating && (
        <Tooltip title="Switch to admin">
          <span>
            <IconButton
              variant={navLinkVariant}
              iconType="person-male-formal"
              size="large"
              aria-label="Switch to admin"
              onClick={() => {
                switchToAdmin();
                closeSidenav();
              }}
            />
          </span>
        </Tooltip>
      )}

      <Tooltip title="Search agents or workplaces">
        <span>
          <IconButton
            variant={navLinkVariant}
            iconType="magnifying-glass"
            size="large"
            aria-label="Open search"
            onClick={openOmnisearch}
          />
        </span>
      </Tooltip>

      {displayNotificationsButton && (
        <Tooltip title="Notifications">
          <span>
            <Badge count={unreadNotificationCount}>
              <IconButton
                variant={navLinkVariant}
                iconType={iconTypeNotificationsButton}
                size="large"
                aria-label="Notifications"
                onClick={onClickNotificationsButton}
              />
            </Badge>
          </span>
        </Tooltip>
      )}

      <ThemeModeToggle size="large" />
    </>
  );
}

export function NavigationMenuFooterMobile(props: NavigationMenuFooterMobileProps) {
  const { closeSidenav } = props;
  const isRebrand = useIsRebrand();
  const { themeMode } = useThemeMode();
  const { admin } = useSession();
  const isDark = themeMode === "dark";
  const navLinkVariant: ButtonVariant = isRebrand && !isDark ? "link" : "linkInverted";
  const {
    displayHelpButton,
    iconTypeHelpButton,
    onClickHelpButton,

    displayNotificationsButton,
    iconTypeNotificationsButton,
    unreadNotificationCount,
    notificationsCenterModalState,
    onClickNotificationsButton,
  } = useNavHeader();

  return (
    <>
      <Stack
        flexGrow={0}
        flexShrink={0}
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        gap={3}
      >
        {admin ? (
          <AdminActions
            closeSidenav={closeSidenav}
            navLinkVariant={navLinkVariant}
            displayNotificationsButton={displayNotificationsButton}
            iconTypeNotificationsButton={iconTypeNotificationsButton}
            unreadNotificationCount={unreadNotificationCount}
            onClickNotificationsButton={onClickNotificationsButton}
          />
        ) : (
          <>
            {displayHelpButton && (
              <Tooltip title="Help Center">
                <Button
                  variant={navLinkVariant}
                  size="large"
                  endIconType={iconTypeHelpButton}
                  onClick={() => {
                    onClickHelpButton();
                    closeSidenav();
                  }}
                >
                  Help
                </Button>
              </Tooltip>
            )}

            {displayNotificationsButton && (
              <Tooltip title="Notifications">
                <span>
                  <Badge count={unreadNotificationCount}>
                    <IconButton
                      variant={navLinkVariant}
                      iconType={iconTypeNotificationsButton}
                      size="large"
                      aria-label="Notifications"
                      onClick={onClickNotificationsButton}
                    />
                  </Badge>
                </span>
              </Tooltip>
            )}

            <ThemeModeToggle size="large" />
          </>
        )}
      </Stack>
      <NotificationsCenterDrawer modalState={notificationsCenterModalState} />
    </>
  );
}
